import { format } from 'date-fns';
import { type IUserTest } from '../../../../types/userTest';
import { type FC } from 'react';
import { IconCheck, IconX, IconCalendar, IconChartBar, IconArrowRight } from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import { PROTECTED_ROUTES } from '../../../../constants/routes';
import clsx from 'clsx';

interface Props {
  result: IUserTest
}

const ResultBox: FC<Props> = ({ result }) => {
  const isApproved = result.correctQuestions > (result.numberOfquestions / 2);
  const percentage = Math.round((result.correctQuestions / result.numberOfquestions) * 100);
  const dateFormatted = format(new Date(result.date), 'dd/MM/yyyy');

  return (
    <Link
      to={`${PROTECTED_ROUTES.TEST}?testId=${result._id}`}
      className="block no-underline group cursor-pointer"
    >
      <div className={clsx(
        'relative overflow-hidden rounded-lg border transition-all duration-300 mb-2',
        'p-2 md:p-3',
        // Mobile: vertical layout, Desktop: horizontal layout
        'flex flex-col md:flex-row md:items-center md:justify-between gap-2 md:gap-3',
        // Light mode
        'bg-white border-gray-200 shadow-sm',
        // Dark mode
        'dark:bg-surface-dark dark:border-borders dark:shadow-lg',
        // Hover effects - more pronounced
        'hover:border-blue-400 dark:hover:border-blue-500',
        'hover:shadow-lg dark:hover:shadow-xl',
        'hover:bg-blue-50/50 dark:hover:bg-blue-900/10',
        // Cursor pointer
        'cursor-pointer'
      )}>
        {/* Status indicator bar (left side) */}
        <div className={clsx(
          'absolute left-0 top-0 bottom-0 w-1 transition-all duration-300',
          isApproved
            ? 'bg-gradient-to-b from-green-500 to-emerald-500'
            : 'bg-gradient-to-b from-red-500 to-rose-500'
        )} />

        {/* Mobile: Compact header row with icon, title, and score */}
        <div className="md:hidden flex items-center justify-between gap-2 w-full">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <div className={clsx(
              'flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center transition-all duration-300',
              isApproved
                ? 'bg-green-100 dark:bg-green-900/30'
                : 'bg-red-100 dark:bg-red-900/30'
            )}>
              {isApproved ? (
                <IconCheck
                  size={14}
                  className="text-green-600 dark:text-green-400"
                  strokeWidth={2.5}
                />
              ) : (
                <IconX
                  size={14}
                  className="text-red-600 dark:text-red-400"
                  strokeWidth={2.5}
                />
              )}
            </div>
            <h3 className={clsx(
              'font-semibold text-sm truncate transition-colors duration-300',
              'text-gray-900 dark:text-white',
              'flex-1 min-w-0'
            )}>
              {result.title}
            </h3>
          </div>
          <div className={clsx(
            'flex items-baseline gap-1 flex-shrink-0',
            isApproved
              ? 'text-green-600 dark:text-green-400'
              : 'text-red-600 dark:text-red-400'
          )}>
            <span className="text-lg font-bold transition-colors duration-300">
              {percentage}%
            </span>
          </div>
        </div>

        {/* Mobile: Date and badge row */}
        <div className="md:hidden flex items-center justify-between gap-2 w-full">
          <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-surface-light">
            <IconCalendar size={11} />
            <span>{dateFormatted}</span>
          </div>
          <div className={clsx(
            'px-2 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wide transition-all duration-300',
            isApproved
              ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
              : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
          )}>
            {isApproved ? 'Aprobado' : 'Suspenso'}
          </div>
        </div>

        {/* Desktop: Icon and title section */}
        <div className="hidden md:flex items-center gap-3 flex-1 min-w-0">
          <div className={clsx(
            'flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-300',
            isApproved
              ? 'bg-green-100 dark:bg-green-900/30'
              : 'bg-red-100 dark:bg-red-900/30'
          )}>
            {isApproved ? (
              <IconCheck
                size={16}
                className="text-green-600 dark:text-green-400"
                strokeWidth={2.5}
              />
            ) : (
              <IconX
                size={16}
                className="text-red-600 dark:text-red-400"
                strokeWidth={2.5}
              />
            )}
          </div>

          <div className="flex-1 min-w-0">
            <h3 className={clsx(
              'font-semibold text-base truncate transition-colors duration-300 mb-0.5',
              'text-gray-900 dark:text-white'
            )}>
              {result.title}
            </h3>
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-surface-light">
                <IconCalendar size={12} />
                <span>{dateFormatted}</span>
              </div>
              {/* Ver test button - appears on hover */}
              <div className={clsx(
                'transition-all duration-300',
                'opacity-0 group-hover:opacity-100',
                'translate-y-[-5px] group-hover:translate-y-0',
                'pointer-events-none group-hover:pointer-events-auto'
              )}>
                <div className={clsx(
                  'flex items-center gap-1 text-xs font-medium',
                  'text-blue-600 dark:text-blue-400',
                  'hover:text-blue-700 dark:hover:text-blue-300',
                  'transition-colors duration-200'
                )}>
                  <span>Ver test</span>
                  <IconArrowRight size={14} strokeWidth={2} />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Desktop: Results section - compact horizontal */}
        <div className="hidden md:flex items-center gap-3 flex-shrink-0">
          {/* Score */}
          <div className="flex items-center gap-2">
            <IconChartBar size={14} className="text-gray-400 dark:text-surface-light" />
            <div className="flex items-baseline gap-1">
              <span className={clsx(
                'text-xl font-bold transition-colors duration-300',
                isApproved
                  ? 'text-green-600 dark:text-green-400'
                  : 'text-red-600 dark:text-red-400'
              )}>
                {percentage}%
              </span>
              <span className={clsx(
                'text-xs text-gray-500 dark:text-surface-light'
              )}>
                ({result.correctQuestions}/{result.numberOfquestions})
              </span>
            </div>
          </div>

          {/* Status badge */}
          <div className={clsx(
            'px-2.5 py-1 rounded-full text-xs font-semibold uppercase tracking-wide transition-all duration-300',
            isApproved
              ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
              : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
          )}>
            {isApproved ? 'Aprobado' : 'Suspenso'}
          </div>
        </div>
      </div>
    </Link>
  );
};

export { ResultBox };
